import { useCallback, useEffect, useRef, useMemo } from 'react';
import {
  setAppSessionId,
  updateAppSessionExtendTime,
  getAppSessionData,
} from '../store/appSessionStore';
import {
  APP_SESSION_EXTEND_THROTTLE,
  APP_SESSION_IDLE_THRESHOLD,
  APP_SESSION_ACTIVITY_CHECK_INTERVAL,
} from '../constants';
import {
  createAppSession as createAppSessionApi,
  extendAppSession as extendAppSessionApi,
  logoutAppSession as logoutAppSessionApi,
} from '../api/appSessionApi';
import { useAppSessionBroadcast } from './useAppSessionBroadcast';

function throttle<T extends (...args: unknown[]) => void>(
  func: T,
  delay: number
): T {
  let lastCall = 0;
  return ((...args: unknown[]) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      func(...args);
    }
  }) as T;
}

export interface UseAppSessionActivityOptions {
  createAppSessionEndpoint: string;
  extendAppSessionEndpoint: string;
  logoutAppSessionEndpoint: string;
  appCode?: string;
  isModalOpen: boolean;
  disableAppSessionManagement: boolean;
  onAppSessionExtend?: () => void | Promise<void>;
  onAppSessionCreate?: (appSessionId: string) => void | Promise<void>;
  onAppSessionLogout?: (isTimeout?: boolean) => void | Promise<void>;
  onSessionCreationError?: (error: unknown) => void | Promise<void>;
}

export function useAppSessionActivity({
  createAppSessionEndpoint,
  extendAppSessionEndpoint,
  logoutAppSessionEndpoint,
  appCode,
  isModalOpen,
  disableAppSessionManagement,
  onAppSessionExtend,
  onAppSessionCreate,
  onAppSessionLogout,
  onSessionCreationError,
}: UseAppSessionActivityOptions) {
  const sessionData = getAppSessionData();
  const lastExtendTimeRef = useRef<number>(
    sessionData?.lastExtendedAt ?? Date.now()
  );
  const lastActivityTimeRef = useRef<number>(Date.now());
  const isManagingSessionRef = useRef<boolean>(false);

  const { broadcastSessionCreated, broadcastSessionExtended } =
    useAppSessionBroadcast({
      disabled: disableAppSessionManagement,
      onSessionCreated: async (sessionId) => {
        setAppSessionId(sessionId);
        lastExtendTimeRef.current = Date.now();
        lastActivityTimeRef.current = Date.now();
        await onAppSessionCreate?.(sessionId);
      },
      onSessionExtended: async (timestamp) => {
        lastExtendTimeRef.current = timestamp;
        updateAppSessionExtendTime();
        await onAppSessionExtend?.();
      },
    });

  const createAppSession = useCallback(async () => {
    if (disableAppSessionManagement || !appCode) {
      return;
    }

    try {
      const response = await createAppSessionApi(
        createAppSessionEndpoint,
        appCode
      );

      const sessionId = response.appSessionId;
      const createdAt = new Date(response.creationTime).getTime();
      const now = Date.now();
      setAppSessionId(sessionId, createdAt);
      lastExtendTimeRef.current = now;
      lastActivityTimeRef.current = now;

      await onAppSessionCreate?.(sessionId);

      broadcastSessionCreated(sessionId);
    } catch (error) {
      console.error('Failed to create app session:', error);
      throw error;
    }
  }, [
    disableAppSessionManagement,
    appCode,
    createAppSessionEndpoint,
    onAppSessionCreate,
    broadcastSessionCreated,
  ]);

  const logoutAppSession = useCallback(
    async (reason: 'user' | 'timeout' = 'user') => {
      if (!appCode) {
        return;
      }

      try {
        await logoutAppSessionApi(logoutAppSessionEndpoint, appCode);
        setAppSessionId(null);
        lastExtendTimeRef.current = 0;
        lastActivityTimeRef.current = Date.now();
        await onAppSessionLogout?.(reason === 'timeout');
      } catch (error) {
        console.error('Failed to logout app session:', error);
        throw error;
      }
    },
    [appCode, logoutAppSessionEndpoint, onAppSessionLogout]
  );

  const evaluateAndAct = useCallback(
    (referenceTime: number) => {
      const timeSinceLastExtend = referenceTime - lastExtendTimeRef.current;
      const isIdle = timeSinceLastExtend >= APP_SESSION_IDLE_THRESHOLD;

      if (isIdle && !isManagingSessionRef.current) {
        isManagingSessionRef.current = true;

        createAppSession()
          .catch(async (err: unknown) => {
            console.error('Failed to create app session:', err);
            // Notify component to handle redirect to platform
            try {
              await onSessionCreationError?.(err);
            } catch (cleanupErr: unknown) {
              console.error(
                'Failed to handle session creation error:',
                cleanupErr
              );
            }
          })
          .finally(() => {
            isManagingSessionRef.current = false;
          });
      } else if (
        timeSinceLastExtend >= APP_SESSION_EXTEND_THROTTLE &&
        !isManagingSessionRef.current &&
        appCode
      ) {
        isManagingSessionRef.current = true;
        const now = Date.now();
        extendAppSessionApi(extendAppSessionEndpoint, appCode)
          .then(async () => {
            lastExtendTimeRef.current = now;
            updateAppSessionExtendTime();

            broadcastSessionExtended(now);

            await onAppSessionExtend?.();
          })
          .catch((err: unknown) => {
            console.error('Failed to extend app session:', err);
          })
          .finally(() => {
            isManagingSessionRef.current = false;
          });
      }
    },
    [
      createAppSession,
      onSessionCreationError,
      extendAppSessionEndpoint,
      appCode,
      onAppSessionExtend,
      broadcastSessionExtended,
    ]
  );

  const throttledHandleActivity = useMemo(
    () =>
      throttle(() => {
        const now = Date.now();
        lastActivityTimeRef.current = now;
        evaluateAndAct(now);
      }, APP_SESSION_ACTIVITY_CHECK_INTERVAL),
    [evaluateAndAct]
  );

  const syncFromServerActivity = useCallback(
    (serverLastActivityTimeMs: number) => {
      lastExtendTimeRef.current = serverLastActivityTimeMs;
      evaluateAndAct(Date.now());
    },
    [evaluateAndAct]
  );

  useEffect(() => {
    if (disableAppSessionManagement || isModalOpen) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        throttledHandleActivity();
      }
    };

    // Add passive event listeners for better performance
    const passiveOptions = { passive: true };
    window.addEventListener(
      'mousemove',
      throttledHandleActivity,
      passiveOptions
    );
    window.addEventListener('keydown', throttledHandleActivity, passiveOptions);
    window.addEventListener('wheel', throttledHandleActivity, passiveOptions);
    window.addEventListener(
      'pointerdown',
      throttledHandleActivity,
      passiveOptions
    );
    window.addEventListener(
      'pointermove',
      throttledHandleActivity,
      passiveOptions
    );
    window.addEventListener('focus', throttledHandleActivity, passiveOptions);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('mousemove', throttledHandleActivity);
      window.removeEventListener('keydown', throttledHandleActivity);
      window.removeEventListener('wheel', throttledHandleActivity);
      window.removeEventListener('pointerdown', throttledHandleActivity);
      window.removeEventListener('pointermove', throttledHandleActivity);
      window.removeEventListener('focus', throttledHandleActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [disableAppSessionManagement, isModalOpen, throttledHandleActivity]);

  return { createAppSession, logoutAppSession, syncFromServerActivity };
}
